import { Controller } from "@hotwired/stimulus"

// Board/List selector controller for assigning processes to lists within accessible boards
// Shows a hierarchical dropdown with boards and their nested lists
export default class extends Controller {
  static targets = ["input", "dropdown", "list", "buttonText", "hint", "icon"]

  static values = {
    boards: Array, // [{id, name, display_name, lists: [{id, name, color}]}]
    itemId: String,
    itemType: String, // 'process' or 'task'
    currentListId: String,
    currentBoardId: String,
    assignUrl: String,
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this.isOpen = false
    this.selectedIndex = 0
    this.flattenedItems = this.buildFlattenedItems()
    this.filteredItems = [...this.flattenedItems]

    // Stable, unique id so the combobox input can point at the listbox and its
    // active row via aria-controls / aria-activedescendant. The partial can
    // render many times per page, so the id must be per-instance.
    this.listboxId = `bls-listbox-${Math.random().toString(36).slice(2, 9)}`
    if (this.hasListTarget) this.listTarget.id = this.listboxId
    if (this.hasInputTarget) this.inputTarget.setAttribute("aria-controls", this.listboxId)

    // Close dropdown when clicking outside
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.handleClickOutside)

    // Escape must close the dropdown first, not the surrounding modal. The task
    // modal is a native <dialog>, so Escape natively fires its `cancel` event —
    // which neither bubbles nor respects a handler on our input. Intercept the
    // keydown in the capture phase while the dropdown is open and swallow it so
    // the dialog's close signal never runs.
    this.handleEscapeCapture = this.handleEscapeCapture.bind(this)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
    document.removeEventListener("keydown", this.handleEscapeCapture, true)
  }

  handleEscapeCapture(event) {
    if (event.key !== "Escape" || !this.isOpen) return
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
    this.close()
  }

  // Build a flat list of selectable items for keyboard navigation
  buildFlattenedItems() {
    const items = [{ type: 'none', id: null, name: 'No List Assigned' }]

    for (const board of (this.boardsValue || [])) {
      for (const list of (board.lists || [])) {
        items.push({
          type: 'list',
          id: list.id,
          name: list.name,
          boardId: board.id,
          boardName: board.display_name || board.name,
          color: list.color
        })
      }
    }

    return items
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target) && this.isOpen) {
      this.close()
    }
  }

  toggle(event) {
    event?.preventDefault()
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.isOpen) return
    this.isOpen = true
    document.addEventListener("keydown", this.handleEscapeCapture, true)
    this.filteredItems = [...this.flattenedItems]
    // Pre-highlight the currently-assigned list so the open menu lands on the
    // active selection rather than always starting at the first row.
    this.selectedIndex = this.currentItemIndex()

    this.applyOpenStyles()
    this.setExpanded(true)

    if (this.hasDropdownTarget) {
      this.positionDropdown()
      this.dropdownTarget.classList.remove("hidden")
    }
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.focus({ preventScroll: true })
    }
    this.render()
  }

  // Reflect open/closed state to assistive tech on both the trigger and the
  // combobox input.
  setExpanded(expanded) {
    const value = expanded ? "true" : "false"
    this.element.querySelector("button")?.setAttribute("aria-expanded", value)
    if (this.hasInputTarget) this.inputTarget.setAttribute("aria-expanded", value)
  }

  positionDropdown() {
    const button = this.element.querySelector("button")
    if (!button || !this.hasDropdownTarget) return

    const rect = button.getBoundingClientRect()
    const dropdown = this.dropdownTarget
    const gap = 4 // breathing room between the trigger and the menu
    const margin = 8 // keep the menu clear of the viewport edge
    const preferredHeight = 360 // search bar + a comfortable list
    const positioningContainer = this.element.closest('[data-resource-preview-target="panel"]')
    const positioningOrigin = positioningContainer?.getBoundingClientRect() || {
      top: 0,
      bottom: window.innerHeight,
      left: 0,
    }

    // Usable room above/below the trigger, once the gap and edge margin are
    // taken out. A transformed preview panel is the containing block for its
    // fixed descendants, so use its bounds instead of viewport coordinates.
    const spaceBelow = positioningOrigin.bottom - rect.bottom - gap - margin
    const spaceAbove = rect.top - positioningOrigin.top - gap - margin

    dropdown.style.left = `${rect.left - positioningOrigin.left}px`
    dropdown.style.width = `${Math.max(rect.width, 288)}px` // min 288px (sm:w-72)

    // Open downward unless there's too little room below AND more room above.
    const openUpward = spaceBelow < preferredHeight && spaceAbove > spaceBelow
    const available = openUpward ? spaceAbove : spaceBelow

    if (openUpward) {
      dropdown.style.top = "auto"
      dropdown.style.bottom = `${positioningOrigin.bottom - rect.top + gap}px`
    } else {
      dropdown.style.bottom = "auto"
      dropdown.style.top = `${rect.bottom - positioningOrigin.top + gap}px`
    }

    // Keep long menus compact when there is ample room, but shrink further when
    // the chosen direction has less than the preferred height.
    dropdown.style.maxHeight = `${Math.max(Math.min(available, preferredHeight), 160)}px`
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    document.removeEventListener("keydown", this.handleEscapeCapture, true)

    this.removeOpenStyles()
    this.setExpanded(false)

    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
      this.dropdownTarget.style.top = ""
      this.dropdownTarget.style.bottom = ""
      this.dropdownTarget.style.left = ""
      this.dropdownTarget.style.width = ""
      this.dropdownTarget.style.maxHeight = ""
    }
  }

  // Highlight the trigger button while the dropdown is open. One edge only:
  // a ring here drew a second outline just outside the slot's own border.
  applyOpenStyles() {
    const button = this.element.querySelector("button")
    button?.classList.add("paper-slot--open")
  }

  removeOpenStyles() {
    const button = this.element.querySelector("button")
    button?.classList.remove("paper-slot--open")
  }

  filter(event) {
    const query = event.target.value.toLowerCase().trim()

    if (query === "") {
      this.filteredItems = [...this.flattenedItems]
    } else {
      this.filteredItems = this.flattenedItems.filter(item => {
        if (item.type === 'none') return false
        const searchText = `${item.boardName} ${item.name}`.toLowerCase()
        return searchText.includes(query)
      })
    }

    this.selectedIndex = 0
    this.render()
  }

  handleKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveSelection(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveSelection(-1)
        break
      case "Enter":
        event.preventDefault()
        this.selectCurrentItem()
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  moveSelection(delta) {
    const totalItems = this.filteredItems.length
    if (totalItems === 0) return

    this.setActive((this.selectedIndex + delta + totalItems) % totalItems)
  }

  // Pointer and keyboard share one highlight, so moving the mouse over a row
  // hands the keyboard its place.
  hoverItem(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (!Number.isNaN(index) && index !== this.selectedIndex) this.setActive(index, { scroll: false })
  }

  selectCurrentItem() {
    const item = this.filteredItems[this.selectedIndex]
    if (!item) return

    if (item.type === 'none') {
      this.assignToList(null)
    } else {
      this.assignToList(item.id)
    }
  }

  selectItem(event) {
    event.preventDefault()
    const listId = event.currentTarget.dataset.listId
    this.assignToList(listId === "" ? null : listId)
  }

  async assignToList(listId) {
    const normalizedListId = listId || null
    const previousListId = this.currentListIdValue

    // Optimistic update: close the menu and reflect the new selection right away
    // so the click feels instant, then reconcile with the server in the
    // background. Close first so the button is back in its resting styling
    // before we snapshot it for a possible rollback.
    this.close()
    const snapshot = this.captureButtonSnapshot()
    const changed = (previousListId || null) !== normalizedListId
    this.updateButtonDisplay(normalizedListId, null, { animate: changed })
    this.currentListIdValue = normalizedListId || ""
    this.setLoading(true)

    try {
      const response = await fetch(this.assignUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json",
        },
        body: JSON.stringify({
          item_type: this.itemTypeValue,
          item_id: this.itemIdValue,
          list_id: normalizedListId,
        }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        console.error("Failed to assign to list:", error)
        this.revertButton(snapshot, previousListId)
        return
      }

      const result = await response.json()

      // Reconcile the optimistic display with the authoritative server response
      // (canonical list name/colour) and apply any side effects.
      this.updateButtonDisplay(normalizedListId, result.list)

      // Update state badge if process was put on hold
      if (result.held) {
        this.updateStateToOnHold()
      }
    } catch (error) {
      console.error("Error assigning to list:", error)
      this.revertButton(snapshot, previousListId)
    } finally {
      this.setLoading(false)
    }
  }

  // Swap the trigger's chevron for a spinner (and back) while a save is in
  // flight, so the background request still has a visible "saving" signal.
  setLoading(isLoading) {
    if (!this.hasIconTarget) return
    this.iconTarget.classList.toggle("ti-chevron-down", !isLoading)
    this.iconTarget.classList.toggle("ti-loader", isLoading)
    this.iconTarget.classList.toggle("animate-spin", isLoading)
  }

  // Capture the trigger's current slot so an optimistic selection can be
  // rolled back verbatim if the server rejects it.
  captureButtonSnapshot() {
    if (!this.hasButtonTextTarget) return null
    const button = this.buttonTextTarget.closest("button")
    return {
      titleHtml: this.buttonTextTarget.innerHTML,
      hint: this.hasHintTarget ? this.hintTarget.textContent : "",
      hintHidden: this.hasHintTarget ? this.hintTarget.hidden : true,
      buttonClass: button ? button.className : null,
      buttonColor: button ? (button.dataset.color ?? null) : null,
      graphicClass: this.graphic()?.className ?? null,
      iconClass: this.hasIconTarget ? this.iconTarget.className : null,
    }
  }

  revertButton(snapshot, previousListId) {
    this.currentListIdValue = previousListId || ""
    if (!snapshot || !this.hasButtonTextTarget) return
    this.buttonTextTarget.innerHTML = snapshot.titleHtml
    if (this.hasHintTarget) {
      this.hintTarget.textContent = snapshot.hint
      this.hintTarget.hidden = snapshot.hintHidden
    }
    const button = this.buttonTextTarget.closest("button")
    if (button && snapshot.buttonClass !== null) button.className = snapshot.buttonClass
    if (button) this.setSlotColor(button, snapshot.buttonColor)
    const graphic = this.graphic()
    if (graphic && snapshot.graphicClass !== null) graphic.className = snapshot.graphicClass
    if (this.hasIconTarget && snapshot.iconClass !== null) this.iconTarget.className = snapshot.iconClass
  }

  graphic() {
    return this.element.querySelector(".paper-board")
  }

  // One slot, two looks — empty (dashed ink, card in the air) and filed (a
  // cream sheet, the list's name marked in its colour, the card landed in that
  // colour). Mirrors paperwork_lists/_board_slot.
  renderSlot({ title, hint, filed, color, animate = false }) {
    this.renderTitle(title, filed, color)
    if (this.hasHintTarget) {
      this.hintTarget.textContent = hint || ""
      this.hintTarget.hidden = !hint
    }
    const button = this.buttonTextTarget.closest("button")
    if (button) {
      button.classList.toggle("paper-slot--filed", filed)
      this.setSlotColor(button, filed ? (color || "gray") : null)
    }
    this.graphic()?.classList.toggle("paper-board--filed", filed)
    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle("text-ink-faint", !filed)
    }
    if (animate && button) this.playSlotMotion(button, filed ? "paper-slot--landing" : "paper-slot--lifting")
  }

  // The slot's title: plain words when nothing is filed, the list's name as a
  // .list-mark when it is. Built from nodes so a list's name is never markup.
  renderTitle(title, filed, color) {
    if (!filed) {
      this.buttonTextTarget.textContent = title
      return
    }
    const mark = document.createElement("span")
    mark.className = "list-mark"
    mark.dataset.color = color || "gray"
    mark.textContent = title
    this.buttonTextTarget.replaceChildren(mark)
  }

  // The tone the slot's landed card wears (.paper-slot[data-color]); cleared
  // when the slot empties so no earlier list's colour lingers on it.
  setSlotColor(button, color) {
    if (color) button.dataset.color = color
    else delete button.dataset.color
  }

  // One play of the pick motion: the card lands in its column (or lifts back
  // off the board). Restart it cleanly if a second pick comes mid-motion.
  playSlotMotion(button, className) {
    button.classList.remove("paper-slot--landing", "paper-slot--lifting")
    void button.offsetWidth
    button.classList.add(className)
    clearTimeout(this.slotMotionTimer)
    this.slotMotionTimer = setTimeout(() => button.classList.remove(className), 700)
  }

  updateButtonDisplay(listId, listData, { animate = false } = {}) {
    if (!this.hasButtonTextTarget) return

    if (!listId) {
      this.renderSlot({ title: "Not on a board yet", hint: "Pick a list to file it", filed: false, animate })
      return
    }

    // Find the item in our flattened list to get board name
    const item = this.flattenedItems.find(i => i.id === listId)
    if (item) {
      this.renderSlot({ title: item.name, hint: item.boardName, filed: true, color: item.color, animate })
    } else if (listData) {
      this.renderSlot({ title: listData.name, hint: "", filed: true, color: listData.color, animate })
    }
  }

  updateStateToOnHold() {
    // Update the bottom State dropdown to reflect on_hold
    const stateSelect = document.getElementById(`paperwork_process_state`)
      || document.querySelector(`select[name="paperwork_process[state]"]`)
    if (stateSelect) {
      stateSelect.value = "on_hold"
    }
  }

  // Index of the row matching the currently-assigned list (or the "No List"
  // row when nothing is assigned), used to pre-highlight on open.
  currentItemIndex() {
    const currentListId = this.currentListIdValue
    const index = this.filteredItems.findIndex(item =>
      item.type === 'none' ? !currentListId : item.id === currentListId
    )
    return index >= 0 ? index : 0
  }

  render() {
    if (!this.hasListTarget) return

    const currentListId = this.currentListIdValue
    let html = ""
    let lastBoardId

    // Lists sit under their board's name, so a row only has to say the list.
    // Two independent states, said two different ways:
    //   - "Which one is chosen": a check at the end of the row.
    //   - "Where the keyboard or mouse is": the row takes the sand wash every
    //     picker uses (setActive).
    this.filteredItems.forEach((item, index) => {
      const isCurrent = item.type === 'none' ? !currentListId : item.id === currentListId
      const optionId = `${this.listboxId}-opt-${index}`
      const current = isCurrent ? "bls-option--current" : ""

      if (item.type === 'none') {
        html += `
          <button type="button"
                  id="${optionId}"
                  role="option"
                  aria-selected="${isCurrent}"
                  class="bls-option ${current}"
                  data-index="${index}"
                  data-action="click->board-list-selector#selectItem mouseenter->board-list-selector#hoverItem"
                  data-list-id="">
            <span class="bls-option__name">Not on a board</span>
            ${isCurrent ? `<i class="ti ti-check bls-option__check text-ink-soft"></i>` : ""}
          </button>
        `
        return
      }

      if (item.boardId !== lastBoardId) {
        lastBoardId = item.boardId
        html += `<div class="bls-group" role="presentation">${this.escapeHtml(item.boardName)}</div>`
      }

      html += `
        <button type="button"
                id="${optionId}"
                role="option"
                aria-selected="${isCurrent}"
                aria-label="${this.escapeHtml(`${item.boardName}: ${item.name}`)}"
                class="bls-option ${current}"
                data-index="${index}"
                data-action="click->board-list-selector#selectItem mouseenter->board-list-selector#hoverItem"
                data-list-id="${item.id}">
          <span class="bls-option__name"><span class="list-mark" data-color="${this.colorName(item.color)}">${this.escapeHtml(item.name)}</span></span>
          ${isCurrent ? `<i class="ti ti-check bls-option__check text-ink-soft"></i>` : ""}
        </button>
      `
    })

    if (this.filteredItems.length === 0) {
      html = `<div class="empty-line empty-line--center"><span class="empty-line__title">No matching lists</span></div>`
    }

    this.listTarget.innerHTML = html
    this.setActive(this.selectedIndex)
  }

  // Move the highlight without rebuilding the menu, so the row under the
  // pointer keeps its hover.
  setActive(index, { scroll = true } = {}) {
    if (!this.hasListTarget) return
    this.selectedIndex = index

    this.listTarget.querySelectorAll(".bls-option").forEach(row => {
      const active = Number(row.dataset.index) === index
      row.classList.toggle("bls-option--active", active)
    })

    // Point the combobox at the active row for screen readers, and keep that
    // row scrolled into view *within the list* — never bubbling the scroll up
    // to the page/modal behind the menu.
    if (this.hasInputTarget) {
      if (this.filteredItems.length > 0) {
        this.inputTarget.setAttribute("aria-activedescendant", `${this.listboxId}-opt-${index}`)
      } else {
        this.inputTarget.removeAttribute("aria-activedescendant")
      }
    }
    if (scroll) this.scrollActiveIntoView()
  }

  // Scroll the highlighted row into view inside the list container only, so
  // keyboard navigation never scrolls the page or modal underneath the menu.
  scrollActiveIntoView() {
    if (!this.hasListTarget) return
    const el = this.listTarget.querySelector(`[data-index="${this.selectedIndex}"]`)
    if (!el) return

    const list = this.listTarget
    // A board's first list scrolls its heading into view with it.
    const heading = el.previousElementSibling?.classList.contains("bls-group") ? el.previousElementSibling : null
    const top = (heading || el).offsetTop
    const bottom = el.offsetTop + el.offsetHeight
    if (top < list.scrollTop) {
      list.scrollTop = top
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight
    }
  }

  // A list's colour name, safe to put in an attribute: lower-case letters only.
  colorName(color) {
    return String(color || "gray").replace(/[^a-z]/g, "") || "gray"
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
